import { create } from 'zustand';
import type { AgentProvider, ServerMsg } from './provider';
import { createWsProvider } from './provider';
import { proxyUrl } from './config';
import { executeToolUse } from './executeToolUse';

export type McpTabStatus = 'idle' | 'connecting' | 'connected' | 'error';

interface McpTabState {
  status: McpTabStatus;
  code: string | null;
  expiresAt: number | null;
  error: string | null;
}

export const useMcpTab = create<McpTabState>(() => ({
  status: 'idle',
  code: null,
  expiresAt: null,
  error: null,
}));

// Module-scope singletons.
let _provider: AgentProvider | null = null;
// In-flight connect promise — concurrent callers (e.g. React StrictMode's
// double-invoked effect) await the SAME open() so none proceeds to send on a
// socket that isn't open yet.
let _connecting: Promise<void> | null = null;
let _pendingCode: ((r: { code: string; expiresAt: number }) => void) | null = null;
let _makeProvider: () => AgentProvider = () => createWsProvider(proxyUrl());

/** TEST SEAM: override the provider factory (call before connectMcpTab). */
export function __setMcpProviderFactory(f: () => AgentProvider): void {
  _makeProvider = f;
}

/** Standing handler — processes incoming server messages on the persistent connection. */
function makeHandler(provider: AgentProvider): (m: ServerMsg) => void {
  return (m: ServerMsg) => {
    if (m.type === 'tool_use') {
      // Live-tab: follow camera so the user sees changes; no placeOrigin so frames
      // land where the agent specifies; log + swallow errors so the standing loop
      // never dies.
      void executeToolUse(provider, m, { followCamera: true }).catch((err) => {
        console.error('[mcpTab] tool_use failed', err);
      });
      return;
    }
    if (m.type === 'pair_code') {
      useMcpTab.setState({ code: m.code, expiresAt: m.expiresAt });
      if (_pendingCode) {
        const resolve = _pendingCode;
        _pendingCode = null;
        resolve({ code: m.code, expiresAt: m.expiresAt });
      }
      return;
    }
    // text / step / done / error / unknown — ignore (no local turn running).
    // Optionally surface errors in status for debugging.
    if (m.type === 'error') {
      useMcpTab.setState({ error: m.message });
    }
  };
}

/** Open the persistent MCP-tab connection + install the standing handler. Idempotent. */
export async function connectMcpTab(): Promise<void> {
  // Already fully connected — nothing to do.
  if (_provider !== null && useMcpTab.getState().status === 'connected') return;
  // A connect is in flight — await the SAME open() rather than starting a second
  // one. Critical: without this, a concurrent caller would return early (because
  // _provider is set) and then send on a socket that hasn't opened yet, dropping
  // the message. (React StrictMode double-invokes the mount effect, so this is
  // hit on every dev mount.)
  if (_connecting) return _connecting;

  _connecting = (async () => {
    useMcpTab.setState({ status: 'connecting', error: null });
    const provider = _makeProvider();
    _provider = provider;

    // Install the standing handler before open() so messages arriving immediately
    // on open are routed correctly.
    provider.onMessage(makeHandler(provider));

    // A dropped socket must not leave the UI showing 'connected'. No auto-reconnect
    // (the 11c UI offers a reconnect); just reflect the dead socket as 'error'.
    provider.onClose?.(() => {
      _provider = null;
      _pendingCode = null;
      useMcpTab.setState({ status: 'error', error: 'connection closed' });
    });

    try {
      await provider.open();
      useMcpTab.setState({ status: 'connected', error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      useMcpTab.setState({ status: 'error', error: message });
      _provider = null;
      throw err;
    }
  })();

  try {
    await _connecting;
  } finally {
    _connecting = null;
  }
}

/** Mint a pairing code on the live connection (connects first if needed). */
export async function requestPairCode(): Promise<{ code: string; expiresAt: number }> {
  await connectMcpTab();
  if (_pendingCode) throw new Error('pair code request already in flight');
  return new Promise((resolve) => {
    // Register the resolver BEFORE sending, because the fake provider in tests
    // calls the onMessage handler synchronously inside send().
    _pendingCode = resolve;
    _provider!.send({ type: 'mint_pair_code' });
  });
}

/** Close the connection (drops the TabSession; pairing ends). */
export function disconnectMcpTab(): void {
  _provider?.close();
  _provider = null;
  _pendingCode = null;
  useMcpTab.setState({ status: 'idle', code: null, expiresAt: null, error: null });
}
